import { ReactElement, useState } from 'react'

import { IconButton, Tooltip } from '@joor/design-system'
import styled from 'styled-components'

import { ReviewPageIds } from '../../review.ids'
import { formatOrderAddress } from '../../utils/addresses.utils'
import { usePaymentOrderInfo } from '../PayView/components/Invoice/invoice.queries'
import { convertGlobalIdToRetailerAccount } from '../PayView/utils'
import SendPaymentRequestModal from 'features/Orders/Review/modals/SendPaymentRequestModal/SendPaymentRequestModal'

interface OrphanTooltipWrapperProps {
  children: ReactElement
  isOrphan?: boolean
  hasRemainingBalance?: boolean
}

const testId = 'send-payment-request-toggle'

const StyledIconButton = styled(IconButton)`
  background: var(--color-secondary-100);
  border: 0;
`

const TooltipWrapper = ({
  children,
  isOrphan,
  hasRemainingBalance,
}: OrphanTooltipWrapperProps) => {
  let tooltipContent
  if (isOrphan) {
    tooltipContent = (
      <>
        Payment requests cannot be sent through
        <br />
        JOOR without assigning a retailer.
      </>
    )
  } else if (!hasRemainingBalance) {
    tooltipContent = <>There is no remaining balance.</>
  } else {
    tooltipContent = <>Send Payment Request</>
  }

  return (
    <Tooltip defaultPlacement="bottom" content={tooltipContent}>
      {children}
    </Tooltip>
  )
}

const SendPaymentRequestActionButton = ({
  disabled,
  isOrphan,
  hasRemainingBalance,
}: {
  disabled?: boolean
  isOrphan?: boolean
  hasRemainingBalance: boolean
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { order } = usePaymentOrderInfo()

  return (
    <>
      <TooltipWrapper
        isOrphan={isOrphan}
        hasRemainingBalance={hasRemainingBalance}
      >
        <StyledIconButton
          iconName="plane"
          onClick={() => {
            setIsModalOpen(!isModalOpen)
          }}
          data-testid={testId}
          id={ReviewPageIds.SendPaymentId}
          disabled={disabled || isOrphan || !hasRemainingBalance}
        />
      </TooltipWrapper>
      {isModalOpen && (
        <SendPaymentRequestModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          orderId={order?.id}
          orderPO={order?.referenceNumber}
          orderCurrency={order?.priceType?.currency?.code}
          retailerId={convertGlobalIdToRetailerAccount(
            order?.retailerProfile?.id,
          )}
          retailerName={order?.retailerProfile?.displayName}
          retailerEmail={
            order?.shipping?.address?.email || order?.retailerProfile?.email
          }
          webOrderId={order?.webOrderLookupId}
          brandId={order?.brand?.id}
          brandName={order?.brand?.name}
          billingAddress={formatOrderAddress(order?.billing?.address)}
          hasRefetchPaymentInfo
        />
      )}
    </>
  )
}

export default SendPaymentRequestActionButton
